import * as L from "lonna"
import { Board, Connection, Item, Note, isItemEndPoint, getEndPointItemId, newNote } from "../../../common/src/domain"
import * as uuid from "uuid"
import { Dispatch } from "../store/board-store"
import { BoardFocus, getSelectedItem } from "./board-focus"
import { installKeyboardShortcut } from "./keyboard-shortcuts"
import { emptySet } from "../../../common/src/sets"

const GAP_X = 3 // em between parent and child
const GAP_Y = 1 // em between siblings

// Mind map: Tab on a note (selected or being edited) adds a connected child note to the right,
// stacked under existing children, and starts editing it. ponytail: no auto re-layout, no new item type.
export function childrenOf(board: Board, parentId: string): Item[] {
    return board.connections
        .filter((c) => isItemEndPoint(c.from) && getEndPointItemId(c.from) === parentId && isItemEndPoint(c.to))
        .map((c) => board.items[getEndPointItemId(c.to as any)])
        .filter((i): i is Item => !!i)
}

export function addChildNote(board: Board, parent: Note, dispatch: Dispatch, focus: L.Atom<BoardFocus>) {
    const siblings = childrenOf(board, parent.id)
    const x = parent.x + parent.width + GAP_X
    const y = siblings.length ? Math.max(...siblings.map((s) => s.y + s.height)) + GAP_Y : parent.y
    const child: Note = {
        ...newNote("", parent.color, x, y, parent.width, parent.height, parent.shape, parent.z),
        containerId: parent.containerId,
        fontSize: parent.fontSize,
        align: parent.align,
    }
    const connection: Connection = {
        id: uuid.v4(),
        from: { id: parent.id, side: "right" },
        to: { id: child.id, side: "left" },
        controlPoints: [],
        action: "connect",
        fromStyle: "none",
        toStyle: "arrow",
        pointStyle: "none",
        locked: false,
        containerId: parent.containerId,
    }
    dispatch({ action: "item.add", boardId: board.id, items: [child], connections: [connection] })
    focus.set({ status: "editing", itemId: child.id })
}

export function itemMindMapHandler(board: L.Property<Board>, focus: L.Atom<BoardFocus>, dispatch: Dispatch) {
    installKeyboardShortcut(
        (e) => e.key === "Tab" && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey,
        () => {
            const f = focus.get()
            const item = f.status === "editing" ? board.get().items[f.itemId] : getSelectedItem(board.get())(f)
            if (item && item.type === "note") addChildNote(board.get(), item, dispatch, focus)
        },
    )
    // Shift-Tab: back to the parent
    installKeyboardShortcut(
        (e) => e.key === "Tab" && e.shiftKey,
        () => {
            const f = focus.get()
            const b = board.get()
            const id = f.status === "editing" ? f.itemId : getSelectedItem(b)(f)?.id
            if (!id) return
            const parentConn = b.connections.find(
                (c) => isItemEndPoint(c.to) && getEndPointItemId(c.to) === id && isItemEndPoint(c.from),
            )
            if (parentConn) {
                const parentId = getEndPointItemId(parentConn.from as any)
                focus.set({ status: "selected", itemIds: new Set([parentId]), connectionIds: emptySet() })
            }
        },
    )
}
